import React, { useEffect, useState } from "react";
import { Activity, Package, Repeat, CreditCard, UserCog } from "lucide-react";
import { Footer } from "../../components/Footer";
import { MiCuentaBanner } from "./MiCuentaBanner";
import { ProfileHero } from "./ProfileHero";
import { ResumenCuenta } from "./ResumenCuenta";
import { Actividad } from "./sections/Actividad";
import { Pedidos } from "./sections/Pedidos";
import { Suscripciones } from "./sections/Suscripciones";
import { MetodosPago } from "./sections/MetodosPago";
import { DetallesCuenta } from "./sections/DetallesCuenta";
import { useTheme } from "../../contexts/ThemeContext";
import { createLogger } from "../../utils/debug";
import "./index.css";

const log = createLogger("MiCuenta");

const NAV_ITEMS = [
  { id: "actividad", label: "Actividad", icon: Activity },
  { id: "pedidos", label: "Pedidos", icon: Package },
  { id: "suscripciones", label: "Suscripciones", icon: Repeat },
  { id: "metodos-pago", label: "Métodos de pago", icon: CreditCard },
  { id: "detalles-cuenta", label: "Detalles de la cuenta", icon: UserCog },
];

const SECTION_TITLES = {
  actividad: "Actividad reciente",
  pedidos: "Pedidos",
  suscripciones: "Suscripciones",
  "metodos-pago": "Métodos de pago",
  "detalles-cuenta": "Detalles de la cuenta",
};

const SIDEBAR_WIDTH = 220;

const MiCuenta = () => {
  const theme = useTheme();
  const { colors, mode } = theme || { colors: {}, mode: "light" };
  const isDark = mode === "dark";
  const [activeSection, setActiveSection] = useState("actividad");

  useEffect(() => {
    log.success(`tab activa: ${activeSection}`);
  }, [activeSection]);

  const pageBg = isDark ? "#0c0e14" : colors.backgroundColorBody || "rgba(0, 0, 0, 0.05)";
  const panelBg = isDark ? "rgba(255,255,255,0.025)" : colors.backgroundColorDetailCourse || "#ffffff";
  const panelBorder = isDark ? "none" : "1px solid #e2e8f0";
  const textPrimary = isDark ? "#f1f5f9" : "#1e293b";
  const textSecondary = isDark ? "#8b8a99" : "#64748b";
  const accent = "#ECB236";
  const navActiveBg = isDark ? "rgba(255,255,255,0.06)" : "#f1f5f9";

  const renderSection = () => {
    switch (activeSection) {
      case "pedidos":
        return <Pedidos />;
      case "suscripciones":
        return <Suscripciones />;
      case "metodos-pago":
        return <MetodosPago />;
      case "detalles-cuenta":
        return <DetallesCuenta />;
      case "actividad":
      default:
        return <Actividad />;
    }
  };

  return (
    <React.Fragment>
      <MiCuentaBanner />
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: pageBg,
          background: isDark
            ? "radial-gradient(ellipse 900px 420px at 12% -10%, rgba(236, 178, 54, 0.14), transparent 60%), " + pageBg
            : pageBg,
        }}
      >
        <div
          style={{
            maxWidth: 1200,
            margin: "0 auto",
            padding: "0 16px 48px",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div style={{ marginTop: "-90px" }}>
            <ProfileHero />
          </div>

          <div
            style={{
              marginTop: 40,
              display: "flex",
              flexWrap: "wrap",
              gap: 24,
              alignItems: "flex-start",
            }}
          >
            {/* Columna izquierda: nav + resumen, ancho fijo real */}
            <div style={{ width: SIDEBAR_WIDTH, flexGrow: 0, flexShrink: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 4, width: SIDEBAR_WIDTH }}>
                {NAV_ITEMS.map((item) => {
                  const Icon = item.icon;
                  const isActive = item.id === activeSection;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        log.info(`click en tab: ${item.id}`);
                        setActiveSection(item.id);
                      }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                        width: SIDEBAR_WIDTH,
                        boxSizing: "border-box",
                        padding: "11px 16px",
                        borderRadius: 12,
                        border: "none",
                        background: isActive ? navActiveBg : "transparent",
                        color: isActive ? textPrimary : textSecondary,
                        fontSize: 14,
                        fontWeight: isActive ? 700 : 500,
                        textAlign: "left",
                        cursor: "pointer",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <Icon size={17} color={isActive ? accent : textSecondary} style={{ flexShrink: 0 }} />
                      <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{item.label}</span>
                    </button>
                  );
                })}
              </div>

              <div style={{ width: SIDEBAR_WIDTH, boxSizing: "border-box" }}>
                <ResumenCuenta />
              </div>
            </div>

            {/* Columna derecha: panel de contenido único */}
            <div style={{ flexGrow: 1, flexShrink: 1, flexBasis: 0, minWidth: 280 }}>
              <div
                style={{
                  border: panelBorder,
                  borderRadius: 22,
                  backgroundColor: panelBg,
                  minHeight: 420,
                  boxSizing: "border-box",
                }}
              >
                <div style={{ padding: "28px 32px 8px" }}>
                  <h2
                    style={{
                      margin: 0,
                      fontFamily: "'Outfit', sans-serif",
                      fontSize: 19,
                      fontWeight: 600,
                      color: textPrimary,
                    }}
                  >
                    {SECTION_TITLES[activeSection]}
                  </h2>
                </div>
                <div style={{ padding: "20px 32px 32px" }}>{renderSection()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </React.Fragment>
  );
};

export default MiCuenta;
